import { METHODS, addDays, buildAdvice, currentReadings, dailySeries, dailyWeather, et0Days, monthOf, targetFor, type Advice, type AdviceRun, type DayWeather, type Reading, type Target } from '@gk/contracts'
import type { ProfileRow, RunRow } from './water.ts'

/** Pure logic of the water screens (no React, no clock): the site's settings, the advice area by area, the table of recent weather, and the water used between two meter readings. Tested in waterUi.test.ts. */
export function siteSettings(profiles: readonly ProfileRow[]): { latitude: number | null; gddBase: number | null; profile: ProfileRow | undefined } {
  const p = profiles.find((x) => x.scope === 'site' && !x.archived_at)
  return { latitude: p?.latitude ?? null, gddBase: p?.gdd_base_c ?? null, profile: p }
}
export const profileFor = (profiles: readonly ProfileRow[], areaType: string): ProfileRow | undefined => profiles.find((x) => x.scope === 'area_type' && x.area_type_code === areaType && !x.archived_at)

export interface AreaForAdvice { id: string; label: string; typeCode: string }
export interface AreaAdvice { area: AreaForAdvice; advice: Advice; target: Pick<Target, 'low' | 'high'> | null; profile: ProfileRow | undefined; readings: number }
const severity = (a: AreaAdvice): number => (a.advice.verdict === 'below' ? 0 : a.advice.verdict === 'above' ? 1 : a.advice.verdict === 'in' ? 2 : 3)

/** The advice of every area that has soil-moisture readings in the last `days` days (a probe the club carries by hand, so "latest" is the last time somebody went), the ones below their target first. */
export function adviceForAreas(o: {
  areas: readonly AreaForAdvice[]; readings: readonly Reading[]; targets: readonly Target[]; profiles: readonly ProfileRow[]; runs: readonly RunRow[]; today: string; days?: number
}): AreaAdvice[] {
  const { latitude } = siteSettings(o.profiles)
  const weather = dailyWeather(o.readings)
  const soil = currentReadings(o.readings).filter((r) => r.measurement_type === 'soil_moisture' && r.area_id)
  const from = addDays(o.today, -((o.days ?? 30) - 1))
  const out: AreaAdvice[] = []
  for (const area of o.areas) {
    const mine = soil.filter((r) => r.area_id === area.id)
    if (!mine.some((r) => r.observed_local_date >= from && r.observed_local_date <= o.today)) continue
    const profile = profileFor(o.profiles, area.typeCode)
    const t = targetFor(o.targets, 'soil_moisture', area.typeCode, monthOf(o.today))
    const target = t ? { low: t.low, high: t.high } : null
    const runs: AdviceRun[] = o.runs.filter((r) => r.area_id === area.id && !r.superseded_by_id).map((r) => ({ date: r.started_local_date, kind: r.kind, depthMm: r.depth_mm, durationMin: r.duration_min, volumeM3: r.volume_m3 }))
    const advice = buildAdvice({ today: o.today, latitude, weather, tdr: dailySeries(mine), target, profile: profile ? { kc: profile.kc, rootZoneMm: profile.root_zone_mm, precipMmH: profile.precip_mm_h } : null, runs })
    out.push({ area, advice, target, profile, readings: mine.length })
  }
  return out.sort((a, b) => severity(a) - severity(b) || a.area.label.localeCompare(b.area.label, 'fr', { numeric: true }))
}

export interface WeatherRow extends DayWeather { et0: number | null; observed: boolean }
/** The last `days` calendar days up to today, one row each, a day with nothing recorded shown as such (a gap is never filled with a guess). */
export function weatherRows(weather: readonly DayWeather[], latitude: number | null, today: string, days = 14): WeatherRow[] {
  const by = new Map(weather.map((w) => [w.date, w]))
  const et0 = new Map(latitude === null ? [] : et0Days(weather, latitude).map((e) => [e.date, e.et0]))
  const out: WeatherRow[] = []
  for (let n = 0; n < days; n++) {
    const date = addDays(today, -n)
    const w = by.get(date)
    out.push({ date, tmax: w?.tmax ?? null, tmin: w?.tmin ?? null, tmean: w?.tmean ?? null, rh: w?.rh ?? null, rain: w?.rain ?? null, et0: et0.get(date) ?? null, observed: !!w })
  }
  return out
}

export interface MeterUse { from: string; to: string; days: number; m3: number; perDay: number }
/** The water used between two consecutive meter readings (the index only goes up): cubic metres, the days between (club days) and the daily rate. A reading lower than the one before is not a use, and says nothing. */
export function meterUse(readings: readonly Pick<Reading, 'measurement_type' | 'observed_at' | 'observed_local_date' | 'value' | 'superseded_by_id'>[]): MeterUse[] {
  const meter = currentReadings(readings).filter((r) => r.measurement_type === 'water_meter').sort((a, b) => (a.observed_at < b.observed_at ? -1 : a.observed_at > b.observed_at ? 1 : 0))
  const out: MeterUse[] = []
  for (let i = 1; i < meter.length; i++) {
    const a = meter[i - 1]!, b = meter[i]!
    if (b.value < a.value) continue
    const days = Math.max(1, Math.round((Date.parse(`${b.observed_local_date}T00:00:00Z`) - Date.parse(`${a.observed_local_date}T00:00:00Z`)) / 86_400_000))
    const m3 = Math.round((b.value - a.value) * 1000) / 1000
    out.push({ from: a.observed_local_date, to: b.observed_local_date, days, m3, perDay: m3 / days })
  }
  return out
}

/** The methods the water page itself uses, for its "Les méthodes et leurs sources" (its advice, its projection, its forecast block's ET0 prévue): every `METHODS`
 *  entry but the Beaufort force, which only the forecast's wind lines print (M24 review: the page named a scale it never uses). */
export const WATER_METHODS = Object.values(METHODS).filter((m) => m.code !== 'beaufort')
